import React from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { createFloorPlanAction } from 'actions/floorPlanActions'
import { notifyHoneybadgerAction } from 'actions/applicationActions'
import { createFloorPlan } from 'api/floorPlanUploads'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import injectSheet from 'react-jss'
import style from './style'
import FileInfo, { prepProps, fileTypeInvalid } from './FileInfo'
import getFileDimensions from './getFileDimensions'
import { COLORS } from 'helpers/globals'
import onMobile from 'helpers/onMobile'
import getRequestError from 'helpers/getRequestError'
import tippy from 'tippy.js'

const initialState = {
  loading: false,
  errorMsg: null,
  infoMsgs: [],
  fileInfo: {},
  disableUpload: false,
  uploadFileName: null
}

class FloorPlanUploader extends React.Component {
  state = { ...initialState, onMobile: onMobile() }

  componentDidMount = _ => tippy(this.refs.infoIcon, { duration: 0, theme: 'topbar', placement: 'right', onShow: instance => this.tooltip = instance })

  componentWillUnmount = _ => this.tooltip && this.tooltip.hide()

  uploadImage = (fileName = null) => {
    const { props: { close, createFloorPlanAction, notifyHoneybadgerAction }, state: { loading, uploadFileName } } = this

    if (loading || (!fileName && !uploadFileName)) return

    this.setState({ loading: true, errorMsg: null })
    const formData = new FormData(ReactDOM.findDOMNode(this.form || this.mobileForm))

    createFloorPlan(formData).then(({ width, height, url }) => {
      createFloorPlanAction(url, { width, height })
      this.setState({ loading: false, infoMsgs: [] })
      close()
    }).catch(err => {
      const { errorMsg, debugMsg } = getRequestError(err)

      notifyHoneybadgerAction({
        message: `FLOOR PLAN UPLOAD ERROR: ${debugMsg}`,
        stack: err.stack
      }, { type: 'FLOOR_PLAN_UPLOAD_IMAGE', skipThrow: true })

      this.setState({ loading: false, infoMsgs: [], fileInfo: { ...this.state.fileInfo, showContactLink: true }, errorMsg })
      setTimeout(_ => { this.setState({ errorMsg: null }) }, 3000)

      if (this.mobileForm) this.mobileImage.value = ""
    })
  }

  cancel = e => {
    e.preventDefault()
    this.props.close()
  }

  _getFileDimensions = async file => {
    try {
      const dimensions = await getFileDimensions(file, this.setInfoMsg)
      this.setState({
        fileInfo: {
          ...this.state.fileInfo,
          ...dimensions
        }
      })
    } catch (err) {
      this.setState({
        fileInfo: {
          ...this.state.fileInfo,
          width: 0,
          height: 0,
          showContactLink: true
        },
        errorMsg: 'Could not get file dimensions, file may be corrupted'
      })
    }
  }

  setInfoMsg = infoMsg => this.setState({ infoMsgs: [...this.state.infoMsgs, infoMsg] })

  updateFileInfo = _ => {
    const file = this.image.files[0]
    if (file) {
      this._getFileDimensions(file)
      this.setState({
        fileInfo: {
          size: file.size,
          type: file.type,
          width: null,
          height: null
        },
        infoMsgs: [],
        disableUpload: fileTypeInvalid(file.type),
        uploadFileName: file.name
      })
    } else this.setState(initialState)
  }

  setDisableUpload = disableUpload => this.setState({ disableUpload })

  updateMobileForm = ({ target: { files } }) => {
    const file = files[0]

    if (file) this.uploadImage(file.name)
  }

  render = _ => {
    const { updateFileInfo, cancel, setDisableUpload, updateMobileForm, state: { loading, errorMsg, uploadFileName, fileInfo,
      infoMsgs, disableUpload, onMobile }, props: { classes } } = this
    return (
      onMobile
      ? <div className={classes.column}>
          <form className={classes.mobileForm} ref={el => this.mobileForm = el} onSubmit={(e) => e.preventDefault()}>
            <input
              ref={el => this.mobileImage = el}
              className={classes.mobileFileInput}
              id='mobileImage'
              type='file'
              name='floor_plan[image]'
              onChange={updateMobileForm}
              accept='image/jpeg,image/bmp,image/png,application/pdf'
            />
            <label
              className={classes.fileInputLabel}
              htmlFor='mobileImage'
            >
              { loading ? <FontAwesomeIcon icon='sync' spin className={classes.mobileLoadingIcon} /> : 'Choose JPG, BMP, PNG, or PDF'}
            </label>
            { errorMsg && (errorMsg === 'Could not process file, file may be corrupted'
              ? <div>
                  <div className={classes.errroMsg}>Could not process file</div>
                  <p style={{ margin: '1rem 0' }}>Need help? <a href={`${IPVM_URL}/contact?&message=Mobile Calculator Floorplan Upload&subject=Mobile Calculator Floorplan Upload`} target='_blank'>Contact us.</a></p>
                </div>
              : <div className={classes.errroMsg}>{ errorMsg }</div>)
            }
          </form>
        </div>
      : <div className={classes.container}>
          <p>
            For planning cameras inside of buildings or using your own background images or maps, add a JPG, PNG or PDF and we'll put it on top of the map.
          </p>
          <div className={classes.tip}>
            <strong>Tip:</strong> You can even turn off the map or satellite backgrounds by selecting the "Blank" background in the menu bar on the left side of the screen.
          </div>
          <form ref={el => this.form = el} className={classes.form}>
            <label>Choose JPG, BMP, PNG, PDF:</label>
            <input
              ref={el => this.image = el}
              className={classes.fileInput}
              id='image'
              type='file'
              name='floor_plan[image]'
              onChange={updateFileInfo}
            />
            <div className={classes.checkboxWrapper}>
              <input
                id='transparencyCheck'
                type='checkbox'
                name='floor_plan[keep_transparency]'
              />
              <label
                className={classes.checkboxLabel}
                htmlFor='transparencyCheck'
              >
                Keep Transparency?
              </label>
              <a className={classes.infoTooltip} title='Keep original file background transparency' ref='infoIcon'>
                <FontAwesomeIcon icon='info-circle' />
              </a>
            </div>
          </form>
          {errorMsg && <div className={classes.errroMsg}>{ errorMsg }</div>}
          <div
            className={classes.infoMsg}
            style={{ display: infoMsgs.includes('multiPagePdf') ? 'block' : 'none' }}
          >
            <FontAwesomeIcon icon='exclamation-circle' color={COLORS.warning} /> We have detected a multi-page PDF, <u>only the first page will be used</u>.
          </div>
          {!!Object.keys(fileInfo).length && <FileInfo {...prepProps(fileInfo)} setDisableUpload={setDisableUpload} />}
          <hr />
          <div className={classes.buttonContainer}>
            <button
              className={classes.submitFormButton}
              onClick={this.uploadImage.bind(this)}
              disabled={loading || !uploadFileName || disableUpload}
            >
              { loading ? <FontAwesomeIcon icon='sync' className={classes.loadingIcon} /> : 'Upload' }
            </button>
            <button
              className={classes.cancelButton}
              onClick={cancel.bind(this)}
            >
              Cancel
            </button>
          </div>
        </div>
    )
  }
}

export const FloorPlanUploaderConfig = { title: 'Add Floor Plan' }
export default connect(null, { createFloorPlanAction, notifyHoneybadgerAction })(
  injectSheet(style)(FloorPlanUploader)
)



// WEBPACK FOOTER //
// ./src/components/FloorPlanUploader/index.js